import { DateRangePicker, Select, ToggleGroup, todayIso, useAsync, api, useStoredState } from "@getaways/extranet-ui";
import { useMemo, useState } from "react";
import { BRANDS } from "./format";

export type Preset = "month" | "last_month" | "crm_period" | "last_crm_period" | "year" | "last_12" | "custom";

export const PRESETS: Record<Preset, string> = {
  month: "This month",
  last_month: "Last month",
  crm_period: "CRM period (21st–20th)",
  last_crm_period: "Last CRM period",
  year: "Year to date",
  last_12: "Last 12 months",
  custom: "Custom dates",
};

const iso = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);

/** The days a preset covers, `to` inclusive. The CRM's period runs from the 21st to the 20th (docs/13 §0b). */
export function presetRange(preset: Exclude<Preset, "custom">, today = todayIso()): { from: string; to: string } {
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const month = m - 1;
  switch (preset) {
    case "month":
      return { from: iso(y, month, 1), to: iso(y, month + 1, 0) };
    case "last_month":
      return { from: iso(y, month - 1, 1), to: iso(y, month, 0) };
    case "crm_period": {
      const start = d >= 21 ? month : month - 1;
      return { from: iso(y, start, 21), to: iso(y, start + 1, 20) };
    }
    case "last_crm_period": {
      const start = (d >= 21 ? month : month - 1) - 1;
      return { from: iso(y, start, 21), to: iso(y, start + 1, 20) };
    }
    case "year":
      return { from: iso(y, 0, 1), to: today };
    case "last_12":
      return { from: iso(y - 1, month, d + 1), to: today };
  }
}

/** The same days one year earlier: the comparison the pages show. */
export const lastYear = (r: { from: string; to: string }) => ({ from: `${Number(r.from.slice(0, 4)) - 1}${r.from.slice(4)}`, to: `${Number(r.to.slice(0, 4)) - 1}${r.to.slice(4)}` });

export interface Filters {
  preset: Preset;
  range: { from: string; to: string };
  basis: "booked" | "departure";
  brand: string;
  market: string;
  supplier: string;
  status: "sales" | "all" | "cancelled";
}

export interface Options {
  brands: string[];
  markets: string[];
  suppliers: string[];
  agents: { email: string; name: string | null }[];
}

/** Filter state of a page, kept per browser so a person finds their view again. */
export function useFilters(key: string): [Filters, (patch: Partial<Filters>) => void] {
  const [preset, setPreset] = useStoredState<Preset>(`reports.${key}.preset`, "month");
  const [basis, setBasis] = useStoredState<"booked" | "departure">(`reports.${key}.basis`, "booked");
  const [custom, setCustom] = useState(() => presetRange("month"));
  const [brand, setBrand] = useState("");
  const [market, setMarket] = useState("");
  const [supplier, setSupplier] = useState("");
  const [status, setStatus] = useState<Filters["status"]>("sales");
  const range = preset === "custom" ? custom : presetRange(preset);
  const filters: Filters = { preset, range, basis, brand, market, supplier, status };
  const update = (p: Partial<Filters>) => {
    if (p.preset !== undefined) setPreset(p.preset);
    if (p.range) {
      setCustom(p.range);
      setPreset("custom");
    }
    if (p.basis) setBasis(p.basis);
    if (p.brand !== undefined) setBrand(p.brand);
    if (p.market !== undefined) setMarket(p.market);
    if (p.supplier !== undefined) setSupplier(p.supplier);
    if (p.status) setStatus(p.status);
  };
  return [filters, update];
}

/** The query string the API reads (ReportFilter in @getaways/reports). */
export function filterQuery(f: Filters, extra: Record<string, string | number | undefined> = {}): string {
  const p = new URLSearchParams({ from: f.range.from, to: f.range.to, basis: f.basis, status: f.status });
  if (f.brand) p.set("brands", f.brand);
  if (f.market) p.set("markets", f.market);
  if (f.supplier) p.set("suppliers", f.supplier);
  for (const [k, v] of Object.entries(extra)) if (v !== undefined && v !== "") p.set(k, String(v));
  return p.toString();
}

export function useOptions(): Options | undefined {
  return useAsync(() => api<Options>("GET", "/v1/options"), []).data;
}

const all = (label: string) => ({ value: "", label });

/** Period, date basis and the common filters, on one row. */
export function FilterBar({ filters, onChange, options, supplierFilter = true, statusFilter = true }: { filters: Filters; onChange: (p: Partial<Filters>) => void; options: Options | undefined; supplierFilter?: boolean; statusFilter?: boolean }) {
  const presets = useMemo(() => (Object.keys(PRESETS) as Preset[]).map((p) => ({ value: p, label: PRESETS[p] })), []);
  return (
    <div className="filters">
      <Select<Preset> value={filters.preset} onChange={(v) => onChange({ preset: v })} options={presets} aria-label="Period" style={{ width: 210 }} />
      <DateRangePicker value={filters.range} onChange={(r) => onChange({ range: r })} />
      <ToggleGroup<"booked" | "departure"> label="Dates are" value={filters.basis} onValueChange={(v) => onChange({ basis: v })} items={[{ value: "booked", label: "Booked" }, { value: "departure", label: "Departing" }]} />
      <Select value={filters.brand} onChange={(v) => onChange({ brand: v })} options={[all("All brands"), ...(options?.brands ?? []).map((b) => ({ value: b, label: BRANDS[b] ?? b }))]} aria-label="Brand" style={{ width: 170 }} />
      <Select value={filters.market} onChange={(v) => onChange({ market: v })} options={[all("All markets"), ...(options?.markets ?? []).map((m) => ({ value: m, label: m }))]} aria-label="Market" style={{ width: 130 }} />
      {supplierFilter && <Select value={filters.supplier} onChange={(v) => onChange({ supplier: v })} options={[all("All suppliers"), ...(options?.suppliers ?? []).map((s) => ({ value: s, label: s }))]} aria-label="Supplier" style={{ width: 170 }} />}
      {statusFilter && (
        <ToggleGroup<Filters["status"]> label="Orders" value={filters.status} onValueChange={(v) => onChange({ status: v })} items={[{ value: "sales", label: "Sold" }, { value: "cancelled", label: "Cancelled" }, { value: "all", label: "All" }]} />
      )}
    </div>
  );
}
